import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Table, Form, Input, Select, Button, Space, Tag, Popconfirm, message, Alert, Spin,
} from 'antd';
import DraggableModal from '../../components/DraggableModal';
import {
  PlusOutlined, UploadOutlined, DownloadOutlined, SearchOutlined,
  SortAscendingOutlined, HolderOutlined, PlusCircleOutlined,
} from '@ant-design/icons';
import api from '../../api';
import { getStoredUser, hasPerm, isSuperAdmin } from '../../userAuth';

const defaultSortRules = [{ field: 'location_name', order: 'asc' }];

export default function Locations() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [partitions, setPartitions] = useState([]);
  const currentUser = getStoredUser();
  const canSeePasswordByLogin = isSuperAdmin(currentUser)
    || hasPerm(currentUser, 'view_location_password');
  const [canSeePasswordByData, setCanSeePasswordByData] = useState(false);
  const canSeePassword = canSeePasswordByLogin || canSeePasswordByData;
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [deleteFilterForm] = Form.useForm();
  const [deleteFilterModalOpen, setDeleteFilterModalOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [tablePagination, setTablePagination] = useState({ current: 1, pageSize: 10 });
  const [searchName, setSearchName] = useState('');
  const [searchPartitionIds, setSearchPartitionIds] = useState([]);
  const [searchStatus, setSearchStatus] = useState(undefined);
  const [sortRules, setSortRules] = useState(defaultSortRules);
  const [sortModalOpen, setSortModalOpen] = useState(false);
  const [draftSortRules, setDraftSortRules] = useState([]);
  const [draggingSortIndex, setDraggingSortIndex] = useState(null);
  const importInputRef = useRef(null);
  const [opLogOpen, setOpLogOpen] = useState(false);
  const [opLogs, setOpLogs] = useState([]);
  const [opLogLoading, setOpLogLoading] = useState(false);
  const [opLogSelected, setOpLogSelected] = useState([]);
  const [ioDialog, setIoDialog] = useState({
    open: false, loading: false, title: '', lines: [],
  });

  const sortFieldOptions = [
    { value: 'location_name', label: '地点名称' },
    { value: 'region_name', label: '所属区域' },
    { value: 'asset_count', label: '关联资产数' },
    { value: 'status', label: '状态' },
    { value: 'id', label: '系统ID' },
  ];

  const buildParams = useCallback((rulesOverride) => {
    const rules = rulesOverride !== undefined ? rulesOverride : sortRules;
    const params = {};
    if (searchName.trim()) params.keyword = searchName.trim();
    if (Array.isArray(searchPartitionIds) && searchPartitionIds.length) {
      params.partition_ids = searchPartitionIds.join(',');
    }
    if (searchStatus !== undefined) params.status = searchStatus;
    const validSort = rules
      .filter(rule => rule?.field && rule?.order)
      .map(rule => `${rule.field}:${rule.order}`);
    if (validSort.length) params.sort_by = validSort.join(',');
    return params;
  }, [sortRules, searchName, searchPartitionIds, searchStatus]);

  const fetchData = useCallback(async (rulesOverride) => {
    setLoading(true);
    try {
      const res = await api.get('/asset-locations', { params: buildParams(rulesOverride) });
      const list = Array.isArray(res) ? res : [];
      setData(list);
      // 兼容：若登录信息里还未带 perm_keys，则退回以接口返回的 password_visible 判断
      setCanSeePasswordByData(list.some(r => r?.password_visible));
      setSelectedRowKeys([]);
      setTablePagination(p => ({ ...p, current: 1 }));
    } catch { message.error('获取存放地点列表失败'); }
    setLoading(false);
  }, [buildParams]);

  const fetchPartitions = useCallback(async () => {
    try {
      const res = await api.get('/partitions');
      setPartitions(Array.isArray(res) ? res : []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchData(); fetchPartitions(); }, [fetchData, fetchPartitions]);

  const handleSearch = () => fetchData();

  const handleReset = () => {
    setSearchName('');
    setSearchPartitionIds([]);
    setSearchStatus(undefined);
  };

  const openSortModal = () => {
    setDraftSortRules([]);
    setSortModalOpen(true);
  };

  const applySortRules = () => {
    const cleaned = draftSortRules.filter(r => r.field && r.order);
    const fields = cleaned.map(r => r.field);
    if (new Set(fields).size !== fields.length) {
      message.warning('排序字段不能重复，请调整后再应用');
      return;
    }
    const next = cleaned.length ? cleaned : [...defaultSortRules];
    setSortRules(next);
    setSortModalOpen(false);
    fetchData(next);
  };

  const restoreDefaultSortRules = () => {
    const next = [...defaultSortRules];
    setSortRules(next);
    setDraftSortRules([]);
    setSortModalOpen(false);
    fetchData(next);
  };

  const handleCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ status: 1, partition_ids: [] });
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      location_name: record.location_name,
      status: record.status,
      password: record.password,
      partition_ids: Array.isArray(record.partition_ids) ? record.partition_ids : [],
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (values.password && !/^\d{6}$/.test(String(values.password))) {
        message.warning('房间密码须为6位纯数字（可为空）');
        return;
      }
      const payload = {
        location_name: values.location_name,
        status: values.status,
        password: values.password,
        partition_ids: values.partition_ids || [],
      };
      if (editing) {
        payload.region_id = editing.region_id;
        await api.put(`/asset-locations/${editing.id}`, payload);
        message.success('更新成功');
      } else {
        await api.post('/asset-locations', payload);
        message.success('创建成功');
      }
      setModalOpen(false);
      fetchData();
    } catch { /* validation or api error */ }
  };

  const handleDownloadImportTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${window.location.origin}/api/asset-locations/excel/template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('下载失败');
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = '存放位置导入模板.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      message.success('模板已下载（分区列为下拉，选项来自当前分区设置）');
    } catch (err) {
      message.error(err.message || '下载模板失败');
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await api.post('/asset-locations/delete-batch', { scope: 'ids', ids: [id] });
      message.success(`删除成功（${res?.deleted_count || 0} 条）`);
      fetchData();
    } catch (err) {
      message.error(err?.message || '删除失败，该地点可能关联了资产');
    }
  };

  const handleDeleteSelected = async () => {
    if (!selectedRowKeys.length) {
      message.warning('请先勾选要删除的地点');
      return;
    }
    try {
      const res = await api.post('/asset-locations/delete-batch', { scope: 'ids', ids: selectedRowKeys });
      message.success(`批量删除成功（${res?.deleted_count || 0} 条）`);
      fetchData();
    } catch (err) {
      message.error(err.message || '删除失败');
    }
  };

  const handleDeleteByFilter = async () => {
    try {
      const values = await deleteFilterForm.validateFields();
      const hasAny = [
        values.keyword,
        (Array.isArray(values.partition_ids) ? values.partition_ids.length : 0) ? values.partition_ids : undefined,
        values.status,
      ].some(v => v !== undefined && v !== null && v !== '');
      if (!hasAny) {
        message.warning('请至少填写一项筛选条件，禁止空白删除');
        return;
      }
      const payload = { scope: 'filtered' };
      if (values.keyword) payload.keyword = values.keyword;
      if (Array.isArray(values.partition_ids) && values.partition_ids.length) payload.partition_ids = values.partition_ids;
      if (values.status !== undefined) payload.status = values.status;
      const res = await api.post('/asset-locations/delete-batch', payload);
      message.success(`按筛选条件删除成功（${res?.deleted_count || 0} 条）`);
      setDeleteFilterModalOpen(false);
      fetchData();
    } catch (err) {
      message.error(err.message || '删除失败');
    }
  };

  const handleExport = async () => {
    setIoDialog({
      open: true,
      loading: true,
      title: '正在导出地点',
      lines: ['请稍候，导出完成前请勿重复提交。'],
    });
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${window.location.origin}/api/asset-locations/excel/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('导出失败');
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `locations_export_${Date.now()}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      setIoDialog({
        open: true,
        loading: false,
        title: '导出完成',
        lines: [
          `成功数：${Number(data.length || 0)}`,
          '更新数：0',
          '跳过数：0',
        ],
      });
    } catch {
      setIoDialog({
        open: true,
        loading: false,
        title: '导出失败',
        lines: ['导出过程中发生错误，请重试。'],
      });
    }
  };

  const handleImportClick = () => importInputRef.current?.click();

  const handleImportFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setIoDialog({
      open: true,
      loading: true,
      title: '正在导入地点',
      lines: ['请稍候，导入完成前请勿重复提交。'],
    });
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', f);
      const r = await fetch(`${window.location.origin}/api/asset-locations/excel/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const j = await r.json();
      if (j.code !== 200) throw new Error(j.msg || '导入失败');
      const { created = 0, updated = 0, errors = [] } = j.data || {};
      if (created === 0 && updated === 0 && errors.length) {
        setIoDialog({
          open: true,
          loading: false,
          title: '导入失败',
          lines: [`导入失败：${errors.slice(0, 3).join('；')}${errors.length > 3 ? '……' : ''}`],
        });
      } else {
        setIoDialog({
          open: true,
          loading: false,
          title: '导入完成',
          lines: [
            `成功数：${Number(created || 0) + Number(updated || 0)}`,
            `更新数：${updated}`,
            `跳过数：${errors.length}`,
          ],
        });
      }
      fetchData();
    } catch (err) {
      setIoDialog({
        open: true,
        loading: false,
        title: '导入失败',
        lines: [err.message || '导入失败，请重试。'],
      });
    }
  };

  const fetchOpLogs = async () => {
    setOpLogLoading(true);
    try {
      const res = await api.get('/data-op-logs', { params: { module: 'locations', page: 1, page_size: 200 } });
      setOpLogs(Array.isArray(res?.items) ? res.items : []);
    } catch (err) {
      message.error(err.message || '获取操作日志失败');
    }
    setOpLogLoading(false);
  };

  const partitionNameMap = useMemo(() => {
    const m = new Map();
    (partitions || []).forEach((p) => { m.set(p.id, p.name); });
    return m;
  }, [partitions]);

  const columns = [
    { title: '地点名称', dataIndex: 'location_name', key: 'location_name' },
    {
      title: '所属分区',
      key: 'partition_names',
      width: 200,
      render: (_, record) => {
        const ids = record.partition_ids || [];
        const names = ids.map((id) => partitionNameMap.get(id)).filter(Boolean);
        return names.length ? names.join('、') : '—';
      },
    },
    { title: '所属区域', dataIndex: 'region_name', key: 'region_name' },
    ...(canSeePassword ? [{
      title: '房间密码',
      dataIndex: 'password',
      key: 'password',
      render: (v) => (v ? v : '无密码/机械钥匙'),
    }] : []),
    {
      title: '关联资产数', dataIndex: 'asset_count', key: 'asset_count',
      render: v => <span style={{ color: '#1890ff' }}>{v ?? 0}</span>,
    },
    {
      title: '状态', dataIndex: 'status', key: 'status',
      render: v => v === 1
        ? <Tag color="success">启用</Tag>
        : <Tag color="error">禁用</Tag>,
    },
    {
      title: '操作', key: 'actions',
      render: (_, record) => {
        const hasAssets = (record.asset_count ?? 0) > 0;
        return (
          <Space>
            <Button type="link" size="small" onClick={() => handleEdit(record)}>编辑</Button>
            {hasAssets ? (
              <Button type="link" size="small" disabled>删除</Button>
            ) : (
              <Popconfirm title="确认删除该地点？" onConfirm={() => handleDelete(record.id)}>
                <Button type="link" size="small" danger>删除</Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, margin: 0 }}>存放地点管理</h1>
        <Space>
          <input
            ref={importInputRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            onChange={handleImportFile}
          />
          <Button icon={<UploadOutlined />} onClick={handleImportClick}>导入</Button>
          <Button onClick={handleDownloadImportTemplate}>下载导入模板</Button>
          <Button icon={<DownloadOutlined />} onClick={handleExport}>导出</Button>
          <Button onClick={() => { setOpLogSelected([]); setOpLogOpen(true); fetchOpLogs(); }}>操作日志</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>新增地点</Button>
        </Space>
      </div>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="存放地点与「分区」关联后，移动端《资产》页选择分区即可查看该分区内所有存放地点下的资产；与区域无必然关系。导入请使用「下载导入模板」（分区列为下拉）。地点名称排序按汉字拼音首字母（A-Z），可点「自定义排序」设置多列优先级。"
      />

      <div style={{ background: '#fff', borderRadius: 8, padding: '16px 24px', marginBottom: 16 }}>
        <Space size="middle" wrap>
          <Input
            placeholder="搜索地点名称"
            style={{ width: 320 }}
            value={searchName}
            onChange={e => setSearchName(e.target.value)}
            onPressEnter={handleSearch}
            allowClear
          />
          <Select
            mode="multiple"
            showSearch
            optionFilterProp="label"
            placeholder="所属分区（可多选）"
            style={{ width: 320 }}
            value={searchPartitionIds}
            onChange={v => setSearchPartitionIds(v)}
            allowClear
            options={(Array.isArray(partitions) ? partitions : []).map(p => ({ value: p.id, label: p.name }))}
          />
          <Select
            placeholder="全部状态"
            style={{ width: 160 }}
            value={searchStatus}
            onChange={v => setSearchStatus(v)}
            allowClear
            options={[
              { value: 1, label: '启用' },
              { value: 0, label: '禁用' },
            ]}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>搜索</Button>
          <Button onClick={handleReset}>重置</Button>
        </Space>
      </div>

      <div style={{ fontSize: 14, color: '#666', marginBottom: 12 }}>
        共 <span style={{ fontSize: 20, fontWeight: 600, color: '#333' }}>{data.length}</span> 个存放地点
        <span style={{ marginLeft: 8, fontSize: 12, color: '#999' }}>
          表头勾选为当前页；点表头勾选框右侧下拉箭头可选「本页可删」或「全部可删」（仅无关联资产的地点）。
        </span>
        <Button style={{ marginLeft: 12 }} icon={<SortAscendingOutlined />} onClick={openSortModal}>
          自定义排序
        </Button>
        <Popconfirm
          title={`仅删除已勾选项：确认删除 ${selectedRowKeys.length} 个地点？`}
          onConfirm={handleDeleteSelected}
          disabled={!selectedRowKeys.length}
        >
          <Button style={{ marginLeft: 12 }} danger disabled={!selectedRowKeys.length}>删除已勾选</Button>
        </Popconfirm>
        <Button style={{ marginLeft: 8 }} danger onClick={() => { deleteFilterForm.resetFields(); setDeleteFilterModalOpen(true); }}>
          按筛选条件删除
        </Button>
      </div>

      <div style={{ background: '#fff', borderRadius: 8, padding: '0 0 16px' }}>
        <Table
          rowKey="id"
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
            // 有关联资产的地点禁止删除：前端直接禁用勾选，避免误操作
            getCheckboxProps: (record) => ({ disabled: (record.asset_count ?? 0) > 0 }),
            selections: [
              {
                key: 'page-deletable',
                text: '勾选本页可删（无关联资产）',
                onSelect: () => {
                  const { current, pageSize } = tablePagination;
                  const start = (current - 1) * pageSize;
                  const slice = data.slice(start, start + pageSize);
                  const keys = slice.filter(r => !(r.asset_count > 0)).map(r => r.id);
                  setSelectedRowKeys(keys);
                },
              },
              {
                key: 'all-deletable',
                text: '勾选全部可删地点（所有页）',
                onSelect: () => {
                  const keys = data.filter(r => !(r.asset_count > 0)).map(r => r.id);
                  setSelectedRowKeys(keys);
                },
              },
              Table.SELECTION_NONE,
            ],
          }}
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={{
            current: tablePagination.current,
            pageSize: tablePagination.pageSize,
            showSizeChanger: true,
            showTotal: (total, range) => `显示 ${range[0]} 到 ${range[1]} 条，共 ${total} 条`,
            onChange: (page, pageSize) => setTablePagination({ current: page, pageSize }),
            style: { padding: '0 16px' },
          }}
        />
      </div>

      <DraggableModal
        title={ioDialog.title || '处理中'}
        open={ioDialog.open}
        closable={!ioDialog.loading}
        maskClosable={false}
        keyboard={!ioDialog.loading}
        footer={ioDialog.loading ? null : [
          <Button key="ok" type="primary" onClick={() => setIoDialog({
            open: false, loading: false, title: '', lines: [],
          })}
          >
            确定
          </Button>,
        ]}
      >
        {ioDialog.loading ? (
          <div style={{ padding: '8px 0', textAlign: 'center' }}>
            <Spin />
            <div style={{ marginTop: 12, color: '#666' }}>{ioDialog.lines?.[0] || '处理中，请稍候...'}</div>
          </div>
        ) : (
          <div style={{ lineHeight: 1.9 }}>
            {(ioDialog.lines || []).map((line, idx) => <div key={`io-line-${idx}`}>{line}</div>)}
          </div>
        )}
      </DraggableModal>

      <DraggableModal
        title="导入导出操作日志"
        open={opLogOpen}
        onCancel={() => setOpLogOpen(false)}
        footer={[
          <Button
            key="exportSelected"
            onClick={async () => {
              if (!opLogSelected.length) { message.warning('请先勾选日志记录'); return; }
              const token = localStorage.getItem('token');
              const r = await fetch(`${window.location.origin}/api/data-op-logs/export-selected-details`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ log_ids: opLogSelected }),
              });
              if (!r.ok) { message.error('导出失败'); return; }
              const blob = await r.blob();
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url; a.download = `locations_op_logs_${Date.now()}.xlsx`; a.click();
              window.URL.revokeObjectURL(url);
            }}
          >
            导出选中详情
          </Button>,
          <Button key="close" onClick={() => setOpLogOpen(false)}>关闭</Button>,
        ]}
        width={980}
      >
        <Table
          rowKey="id"
          size="small"
          loading={opLogLoading}
          dataSource={opLogs}
          rowSelection={{ selectedRowKeys: opLogSelected, onChange: setOpLogSelected }}
          pagination={{ pageSize: 10 }}
          columns={[
            { title: '时间', dataIndex: 'create_time', key: 'create_time', width: 170 },
            { title: '类型', dataIndex: 'action', key: 'action', width: 80, render: v => (v === 'import' ? '导入' : '导出') },
            { title: '文件', dataIndex: 'file_name', key: 'file_name', width: 200 },
            { title: '总数', dataIndex: 'total_count', key: 'total_count', width: 70 },
            { title: '新增/成功', dataIndex: 'created_count', key: 'created_count', width: 90 },
            { title: '更新', dataIndex: 'updated_count', key: 'updated_count', width: 70 },
            { title: '跳过', dataIndex: 'skipped_count', key: 'skipped_count', width: 70 },
            { title: '操作人', dataIndex: 'operator_name', key: 'operator_name', width: 90 },
            {
              title: '操作',
              key: 'actions',
              render: (_, r) => (
                <Button
                  type="link"
                  onClick={async () => {
                    const token = localStorage.getItem('token');
                    const resp = await fetch(`${window.location.origin}/api/data-op-logs/${r.id}/export-details`, {
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    if (!resp.ok) { message.error('导出失败'); return; }
                    const blob = await resp.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url; a.download = `locations_op_log_${r.id}.xlsx`; a.click();
                    window.URL.revokeObjectURL(url);
                  }}
                >
                  导出详情
                </Button>
              ),
            },
          ]}
        />
      </DraggableModal>

      <DraggableModal
        title="按筛选条件删除地点（需重新填写）"
        open={deleteFilterModalOpen}
        onOk={handleDeleteByFilter}
        onCancel={() => setDeleteFilterModalOpen(false)}
        okText="确认删除"
        cancelText="取消"
        destroyOnClose
      >
        <div style={{ marginBottom: 12, color: '#cf1322' }}>
          为避免误删，必须重新填写筛选条件；禁止空白条件删除。
        </div>
        <Form form={deleteFilterForm} layout="vertical">
          <Form.Item name="keyword" label="地点关键词">
            <Input allowClear placeholder="地点名称" />
          </Form.Item>
          <Form.Item name="partition_ids" label="所属分区">
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              allowClear
              placeholder="请选择分区（可多选）"
              options={(Array.isArray(partitions) ? partitions : []).map(p => ({ value: p.id, label: p.name }))}
            />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select allowClear options={[{ value: 1, label: '启用' }, { value: 0, label: '禁用' }]} />
          </Form.Item>
        </Form>
      </DraggableModal>

      <DraggableModal
        title="自定义排序（多条件优先级，拖拽调整顺序）"
        open={sortModalOpen}
        onOk={applySortRules}
        onCancel={() => setSortModalOpen(false)}
        okText="应用排序"
        cancelText="取消"
        width={760}
        destroyOnClose
      >
        <div style={{ marginBottom: 12, color: '#666' }}>
          条件1 优先级最高；字段不可重复。
        </div>
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {draftSortRules.map((rule, idx) => (
            <Space
              key={`loc-sort-${idx}`}
              wrap
              draggable
              onDragStart={() => setDraggingSortIndex(idx)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (draggingSortIndex === null) return;
                const next = [...draftSortRules];
                const [moved] = next.splice(draggingSortIndex, 1);
                next.splice(idx, 0, moved);
                setDraftSortRules(next);
                setDraggingSortIndex(null);
              }}
              onDragEnd={() => setDraggingSortIndex(null)}
              style={{
                width: '100%',
                padding: '8px 10px',
                border: draggingSortIndex === idx ? '1px dashed #1677ff' : '1px solid #f0f0f0',
                borderRadius: 6,
                background: draggingSortIndex === idx ? '#f0f7ff' : '#fff',
              }}
            >
              <HolderOutlined style={{ color: '#999', cursor: 'grab' }} />
              <span style={{ color: '#666' }}>排序条件{idx + 1}</span>
              <Select
                style={{ width: 200 }}
                value={rule.field}
                onChange={(v) => {
                  const next = [...draftSortRules];
                  next[idx] = { ...next[idx], field: v };
                  setDraftSortRules(next);
                }}
                options={sortFieldOptions.filter(opt => {
                  const used = draftSortRules
                    .map((item, i) => (i === idx ? null : item.field))
                    .filter(Boolean);
                  return !used.includes(opt.value);
                })}
              />
              <Select
                style={{ width: 120 }}
                value={rule.order}
                onChange={(v) => {
                  const next = [...draftSortRules];
                  next[idx] = { ...next[idx], order: v };
                  setDraftSortRules(next);
                }}
                options={[
                  { value: 'asc', label: '升序' },
                  { value: 'desc', label: '降序' },
                ]}
              />
              <Button
                onClick={() => setDraftSortRules(draftSortRules.filter((_, i) => i !== idx))}
              >
                删除
              </Button>
            </Space>
          ))}
          <Space>
            <Button
              icon={<PlusCircleOutlined />}
              onClick={() => {
                const used = draftSortRules.map(r => r.field);
                const firstUnused = sortFieldOptions.find(opt => !used.includes(opt.value));
                if (!firstUnused) {
                  message.info('可选排序字段已全部使用');
                  return;
                }
                setDraftSortRules([...draftSortRules, { field: firstUnused.value, order: 'asc' }]);
              }}
              style={{ width: 140 }}
            >
              增加排序条件
            </Button>
            <Button onClick={restoreDefaultSortRules}>
              恢复默认排序
            </Button>
          </Space>
        </Space>
      </DraggableModal>

      <DraggableModal
        title={editing ? '编辑地点' : '新增地点'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="location_name" label="地点名称" rules={[{ required: true, message: '请输入地点名称' }]}>
            <Input placeholder="请输入地点名称" />
          </Form.Item>
          <Form.Item name="partition_ids" label="所属分区">
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              allowClear
              placeholder="请选择分区（决定移动端资产页「分区」筛选范围，与区域无关）"
              options={(Array.isArray(partitions) ? partitions : []).map(p => ({ value: p.id, label: p.name }))}
            />
          </Form.Item>
          {canSeePassword && (
            <Form.Item
              name="password"
              label="房间密码"
              extra="6位纯数字；不填表示无密码/机械钥匙"
            >
              <Input placeholder="如：123456（可为空）" maxLength={6} inputMode="numeric" />
            </Form.Item>
          )}
          <Form.Item name="status" label="状态" initialValue={1}>
            <Select options={[{ value: 1, label: '启用' }, { value: 0, label: '禁用' }]} />
          </Form.Item>
        </Form>
      </DraggableModal>
    </div>
  );
}
